/**
 * This module provides functions to interact with the organization-related API endpoints.
 * It uses axios for HTTP requests to communicate with the backend server.
 */


import { apiGet, apiPost, validateRequiredParams } from '@/services/BaseApiService';

const baseUrl = '/organization/';

/**
 * Retrieves an organization's details by its ID.
 * 
 * @param {string} id - The unique identifier of the organization to retrieve.
 * @returns {Promise<Object>} The organization's details if the retrieval is successful.
 * @throws Will throw an error if the `id` parameter is missing or if the retrieval fails.
 */
export const getOrganization = async (id) => {
  validateRequiredParams({ id });
  return apiGet(`${baseUrl}getorganization`, { id });
};

export const getOrganizationParents = async (id) => {
  validateRequiredParams({ id });
  return apiGet(`${baseUrl}getorganizationparents`, { id });
};

/**
 * Retrieves the affiliates of an organization by its ID.
 * 
 * @param {string} id - The unique identifier of the organization to retrieve the affiliates for.
 * @returns {Promise<Object>} The affiliates of the organization if the retrieval is successful.
 * @throws Will throw an error if the `id` parameter is missing or if the retrieval fails.
 */
export const getAffiliates = async (id) => {
  validateRequiredParams({ id });
  return apiGet(`${baseUrl}getaffiliates`, { id });
};

/**
 * Inserts an organization.
 * 
 * @param {Object} organization - The organization object to insert.
 * @returns {Promise<Object>} The response data if the insertion is successful.
 * @throws Will throw an error if the `organization` parameter is missing or if the insertion fails.
 */
export const insertOrganization = async (organization) => {
  validateRequiredParams({ organization });
  return apiPost(`${baseUrl}insertorganization`, organization);
};

/**
 * Updates an organization.
 * 
 * @param {Object} organization - The organization object to update.
 * @returns {Promise<Object>} The response data if the update is successful.
 * @throws Will throw an error if the `organization` parameter is missing or if the update fails.
 */
export const updateOrganization = async (organization) => {
  validateRequiredParams({ organization });
  return apiPost(`${baseUrl}updateorganization`, organization);
};



/**
 * Retrieves the contacts of an organization by its ID.
 * 
 * @param {string} id - The unique identifier of the organization to retrieve the contacts for.
 * @returns {Promise<Object>} The contacts of the organization if the retrieval is successful.
 * @throws Will throw an error if the `id` parameter is missing or if the retrieval fails.
 */
export const getContacts = async (id, activeOnly) => {
  validateRequiredParams({ id });
  return apiGet(`${baseUrl}getcontacts`, { id, activeOnly });
};

export const getAvailableThirdPartyAssociates = async (organizationId) => {
  validateRequiredParams({ organizationId });
  return apiGet(`${baseUrl}getavailablethirdpartyassociates`, { id: organizationId });
};

export const addThirdPartyAssociate = async (organizationId, thirdPartyAssociateId) => {
  validateRequiredParams({ organizationId, thirdPartyAssociateId });
  return apiPost(`${baseUrl}addthirdpartyassociate?id=${encodeURIComponent(organizationId)}&tpaId=${encodeURIComponent(thirdPartyAssociateId)}`);
};

export const deleteThirdPartyAssociate = async (organizationId, thirdPartyAssociateId) => {
  validateRequiredParams({ organizationId, thirdPartyAssociateId });
  return apiPost(`${baseUrl}deletethirdpartyassociate?id=${encodeURIComponent(organizationId)}&tpaId=${encodeURIComponent(thirdPartyAssociateId)}`);
};

